import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { Player } from "../../api/models";
import { PButton } from "../../ui/PButton";
import { PSelectButton } from "../../ui/PSelectButton";
import { PSidebar } from "../../ui/PSidebar";
import { CountHeader, PAccordion, PPagedList } from "../../ui/PWidgets";
import { useToast } from "../../ui/toast";
import { useActivities, useCreateGreet, useGreets, type Activity, type Greet } from "../profile-api";

dayjs.extend(utc);

// `momentLocalPipe`: THE STORED UTC INSTANT IN LOCAL TIME
export const localStamp = (v: string | undefined) => (v ? dayjs.utc(v).local().format("MMM DD, YYYY h:mm A") : "");

const SIDEBAR_WIDTH = { xs: "100%", md: "30rem" };

// ACTIVITIES: "ACTIVITY ON {NAME}" OVER A PAGED LIST (15 A PAGE, NEWEST FIRST). OPENING
// RETURNS TO PAGE 1.
export function ActivitiesSidebar({ player, open, onClose }: { player: Player; open: boolean; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPage(1);
  }
  const activities = useActivities(player, page);
  const data = activities.data;
  return (
    <PSidebar open={open} onClose={onClose} position="right" width={SIDEBAR_WIDTH} ariaLabel="Activities">
      <div className="parent">
        <div className="child">
          <div className="text-lg font-bold uppercase text-center">Activity on {player.name}</div>
        </div>
        <div className="child-grow card mt-4 h-full p-0 overflow-x-hidden overflow-y-auto">
          <PPagedList
            page={data?.page ?? page}
            totalPages={data?.totalPages ?? 1}
            totalRows={data?.totalRows ?? 0}
            rows={data?.rows}
            loading={activities.isPending}
            error={activities.isError}
            emptyMessage="No activities available"
            onPage={setPage}
            rowKey={(_, i) => i}
            render={(a) => <ActivityRow activity={a} />}
          />
        </div>
      </div>
    </PSidebar>
  );
}

// `activity-template`: A GREEN `task_alt` WHEN COMPLETED (INVISIBLE OTHERWISE, KEEPING ITS
// SPACE), TYPE / SUBTYPE, THE COMPLETED DATE AND HOST AT THE RIGHT, THEN THE DESCRIPTION
function ActivityRow({ activity: a }: { activity: Activity }) {
  return (
    <>
      <div className="w-full flex align-items-start justify-content-between">
        <div className="flex align-items-center justify-content-start">
          <div className={a.completed ? "text-green-500" : "opacity-0"}>
            <span className="material-symbols-outlined mr-2 fadein animation-duration-1000">task_alt</span>
          </div>
          <div>
            <div className="font-bold">{a.type}</div>
            <div className="text-sm text-500 font-bold">{a.subtype}</div>
          </div>
        </div>
        <div className="flex flex-column align-items-end justify-content-end">
          <div>
            <div className="uppercase font-bold text-xs">{localStamp(a.completedDate)}</div>
            <div className="uppercase font-bold text-xs text-500 mt-1">{a.hostName}</div>
          </div>
        </div>
      </div>
      <div className="pt-2 pl-1 pr-2 text-sm">{a.description}</div>
    </>
  );
}

// GREETS: AN OPEN "GREET" ACCORDION (COMPLETED / NOT FOUND, A 150-CHARACTER MESSAGE, ADD) OVER
// THE PAGED LIST (5 A PAGE). ANGULAR ALLOWS AN EMPTY MESSAGE, AND SO DOES THIS.
export function GreetsSidebar(props: { player: Player; open: boolean; onClose: () => void; count: number | undefined; onCount: (n: number) => void }) {
  const { player, open, onClose, count, onCount } = props;
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"COMPLETED" | "NOT-FOUND">("COMPLETED");
  const [message, setMessage] = useState("");
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPage(1);
  }
  const greets = useGreets(player.cmsPlayerId || undefined, page, open);
  const create = useCreateGreet(player.cmsPlayerId || undefined);
  const data = greets.data;
  const total = data?.totalRows;
  useEffect(() => {
    if (total !== undefined) onCount(total);
  }, [total, onCount]);

  const add = () =>
    create.mutate(
      { status, message },
      {
        onSuccess: () => {
          toast("success", "Add Greet", "Greet was successfully added");
          setStatus("COMPLETED");
          setMessage("");
          setPage(1);
        },
        onError: () => toast("error", "Add Greet", "Greet was not successfully added, please try again."),
      },
    );

  return (
    <PSidebar open={open} onClose={onClose} position="right" width={SIDEBAR_WIDTH} ariaLabel="Greets">
      <div className="parent">
        <div className="child">
          <PAccordion className="mt-3" header={<CountHeader label="Greet" count={count ?? ""} />}>
            <div>
              <PSelectButton
                ariaLabel="Greet status"
                value={status}
                onChange={setStatus}
                options={[
                  { label: "Completed", value: "COMPLETED" },
                  { label: "Not Found", value: "NOT-FOUND" },
                ]}
              />
            </div>
            <div className="mt-4 flex flex-column">
              <label className="font-bold" htmlFor="greet-message">
                Message:
              </label>
              <textarea id="greet-message" className="p-inputtext w-full mt-1" style={{ resize: "none" }} rows={5} maxLength={150} value={message} onChange={(e) => setMessage(e.target.value)} />
            </div>
            <div className="mt-4">
              <PButton label="Add" className="w-full" hostElement loading={create.isPending} onClick={add} />
            </div>
          </PAccordion>
        </div>
        <div className="child-grow card mt-4 h-full p-0 overflow-x-hidden overflow-y-auto">
          <PPagedList
            page={data?.page ?? page}
            totalPages={data?.totalPages ?? 1}
            totalRows={data?.totalRows ?? 0}
            rows={data?.rows}
            loading={greets.isPending}
            error={greets.isError}
            emptyMessage="No greets available"
            onPage={setPage}
            rowKey={(g, i) => g.id ?? i}
            render={(g) => <GreetRow greet={g} />}
          />
        </div>
      </div>
    </PSidebar>
  );
}

// `greet-template`: STATUS AND TIMESTAMP, THEN HOST AND (WHEN KNOWN) FLOOR LOCATION, THEN THE
// MESSAGE. A NULL LOCATION IS SIMPLY LEFT OUT - ANGULAR'S `.length` THROWS ON IT.
function GreetRow({ greet: g }: { greet: Greet }) {
  return (
    <div>
      <div className="flex align-items-center justify-content-between">
        <div className="text-600 font-bold">{(g.status ?? "").toUpperCase()}</div>
        <div className="text-600 flex flex-row align-items-center">
          <span className="material-symbols-outlined mr-2">calendar_month</span>
          <div>{localStamp(g.created_at)}</div>
        </div>
      </div>
      <div className="mt-2 text-600 flex flex-row align-items-center justify-content-between">
        <div className="flex flex-row align-items-center">
          <span className="material-symbols-rounded">account_circle</span>
          <div>{g.hostName}</div>
        </div>
        {!!g.egm_floor_location?.length && (
          <div className="flex flex-row align-items-center">
            <span className="material-symbols-rounded">person_pin_circle</span>
            <div>{g.egm_floor_location}</div>
          </div>
        )}
      </div>
      <div className="mt-3 text-600 flex flex-row align-items-center">
        <div>{g.message}</div>
      </div>
    </div>
  );
}
